/**
 * Dashboard Page Component
 *
 * Main dashboard page that assembles all stats cards, chart, Google status,
 * and webhook status into a cohesive layout. Fetches data on mount and
 * provides manual refresh capability.
 *
 * Layout:
 * - Top row: TodaysPingsCard | FailureRateCard (two-column responsive)
 * - Middle: TrendChart (full width)
 * - Bottom row: GoogleConnectionCard | WebhookStatusCard (two-column responsive)
 *
 * @see docs/PRD.md for dashboard requirements
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Layout,
  Banner,
  Button,
  InlineStack,
  Box,
} from '@shopify/polaris';
import { RefreshIcon } from '@shopify/polaris-icons';

import { TodaysPingsCard } from '../components/TodaysPingsCard';
import { FailureRateCard } from '../components/FailureRateCard';
import { TrendChart } from '../components/TrendChart';
import { GoogleConnectionCard } from '../components/GoogleConnectionCard';
import { WebhookStatusCard } from '../components/WebhookStatusCard';
import { BillingStatusCard } from '../components/BillingStatusCard';

import {
  fetchDashboardStats,
  fetchGoogleStatus,
  fetchWebhookStatus,
  fetchBillingStatus,
} from '../lib/api-client';
import type {
  DashboardStats,
  GoogleConnectionStatus,
  WebhookStatus,
  BillingStatus,
} from '../types/api';

// =============================================================================
// Types
// =============================================================================

interface DashboardState {
  /** Dashboard stats data */
  stats: DashboardStats | null;
  /** Google connection status */
  googleStatus: GoogleConnectionStatus | null;
  /** Webhook subscription status */
  webhookStatus: WebhookStatus | null;
  /** Billing status data */
  billingStatus: BillingStatus | null;
  /** Whether initial data is loading */
  loading: boolean;
  /** Whether a refresh is in progress */
  refreshing: boolean;
  /** Error message for stats fetch */
  statsError: string | null;
  /** Error message for Google status fetch */
  googleError: string | null;
  /** Error message for webhook status fetch */
  webhookError: string | null;
  /** Error message for billing status fetch */
  billingError: string | null;
}

// =============================================================================
// Dashboard Component
// =============================================================================

/**
 * Main Dashboard page component.
 *
 * Features:
 * - Fetches all dashboard data on mount via api-client
 * - Shows loading skeletons while data loads
 * - Shows error banners if API calls fail
 * - Refresh button for manual data refresh
 * - Responsive two-column layout for cards
 * - Full-width trend chart
 */
export function Dashboard() {
  const [state, setState] = useState<DashboardState>({
    stats: null,
    googleStatus: null,
    webhookStatus: null,
    billingStatus: null,
    loading: true,
    refreshing: false,
    statsError: null,
    googleError: null,
    webhookError: null,
    billingError: null,
  });

  /**
   * Fetch all dashboard data
   */
  const fetchAllData = useCallback(async (isRefresh = false) => {
    // Set loading or refreshing state
    setState((prev) => ({
      ...prev,
      loading: !isRefresh,
      refreshing: isRefresh,
      // Clear errors on refresh
      ...(isRefresh && {
        statsError: null,
        googleError: null,
        webhookError: null,
        billingError: null,
      }),
    }));

    // Fetch all data in parallel
    const [statsResult, googleResult, webhookResult, billingResult] = await Promise.all([
      fetchDashboardStats(),
      fetchGoogleStatus(),
      fetchWebhookStatus(),
      fetchBillingStatus(),
    ]);

    // Update state with results
    setState((prev) => ({
      ...prev,
      loading: false,
      refreshing: false,
      stats: statsResult.success ? statsResult.data : prev.stats,
      googleStatus: googleResult.success ? googleResult.data : prev.googleStatus,
      webhookStatus: webhookResult.success ? webhookResult.data : prev.webhookStatus,
      billingStatus: billingResult.success ? billingResult.data : prev.billingStatus,
      statsError: statsResult.success ? null : statsResult.error.error,
      googleError: googleResult.success ? null : googleResult.error.error,
      webhookError: webhookResult.success ? null : webhookResult.error.error,
      billingError: billingResult.success ? null : billingResult.error.error,
    }));
  }, []);

  /**
   * Handle manual refresh
   */
  const handleRefresh = useCallback(() => {
    fetchAllData(true);
  }, [fetchAllData]);

  /**
   * Handle status change from child components (e.g., Google connect/disconnect)
   */
  const handleStatusChange = useCallback(() => {
    // Refresh data after status changes
    fetchAllData(true);
  }, [fetchAllData]);

  // Fetch data on mount
  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const {
    stats,
    googleStatus,
    webhookStatus,
    billingStatus,
    loading,
    refreshing,
    statsError,
    googleError,
    webhookError,
    billingError,
  } = state;

  // Check if there are any global errors to show
  const hasGlobalError = statsError && googleError && webhookError && billingError;

  // Check if free tier user is approaching limit (> 80% usage)
  const isFreeTier = billingStatus?.tier === 'free';
  const usagePercentage = isFreeTier && billingStatus.dailyLimit
    ? (billingStatus.dailyUsed / billingStatus.dailyLimit) * 100
    : 0;
  const showUpgradeBanner = isFreeTier && usagePercentage > 80;

  return (
    <div>
      <Box paddingBlockEnd="400">
        <InlineStack align="space-between" blockAlign="center">
          <h1 style={{ fontSize: '20px', fontWeight: 600, margin: 0 }}>Dashboard</h1>
          <Button
            icon={RefreshIcon}
            onClick={handleRefresh}
            loading={refreshing}
            accessibilityLabel="Refresh dashboard data"
          >
            Refresh
          </Button>
        </InlineStack>
      </Box>
      <Layout>
        {/* Global error banner when all fetches fail */}
        {hasGlobalError && (
          <Layout.Section>
            <Banner
              title="Unable to load dashboard data"
              tone="critical"
              action={{
                content: 'Retry',
                onAction: handleRefresh,
              }}
            >
              Please check your connection and try again. If the problem
              persists, contact support.
            </Banner>
          </Layout.Section>
        )}

        {/* Free tier upgrade banner when usage > 80% */}
        {showUpgradeBanner && !loading && (
          <Layout.Section>
            <Banner
              title="You're approaching your daily limit"
              tone="warning"
            >
              You've used {billingStatus?.dailyUsed} of {billingStatus?.dailyLimit} pings today.
              Upgrade to Pro for unlimited pings and never miss an indexing opportunity.
            </Banner>
          </Layout.Section>
        )}

        {/* Billing status card - prominent position for free users */}
        {isFreeTier && !loading && (
          <Layout.Section>
            <BillingStatusCard
              initialStatus={billingStatus}
              onStatusChange={handleStatusChange}
            />
          </Layout.Section>
        )}

        {/* Top row: TodaysPingsCard and FailureRateCard */}
        <Layout.Section>
          <InlineStack gap="400" wrap={false} blockAlign="stretch">
            <Box width="50%">
              <TodaysPingsCard
                stats={stats}
                googleStatus={googleStatus}
                loading={loading}
                error={!hasGlobalError ? statsError : null}
              />
            </Box>
            <Box width="50%">
              <FailureRateCard
                stats={stats}
                loading={loading}
                error={!hasGlobalError ? statsError : null}
              />
            </Box>
          </InlineStack>
        </Layout.Section>

        {/* Middle row: TrendChart spanning full width */}
        <Layout.Section>
          <TrendChart
            data={stats?.trend ?? null}
            loading={loading}
            error={!hasGlobalError ? statsError : null}
          />
        </Layout.Section>

        {/* Bottom row: GoogleConnectionCard and WebhookStatusCard */}
        <Layout.Section>
          <InlineStack gap="400" wrap={false} blockAlign="stretch">
            <Box width="50%">
              <GoogleConnectionCard
                status={googleStatus}
                loading={loading}
                error={!hasGlobalError ? googleError : null}
                onStatusChange={handleStatusChange}
              />
            </Box>
            <Box width="50%">
              <WebhookStatusCard
                status={webhookStatus}
                loading={loading}
                error={!hasGlobalError ? webhookError : null}
                onStatusChange={handleStatusChange}
              />
            </Box>
          </InlineStack>
        </Layout.Section>

        {/* Billing status card for Pro users - less prominent position at bottom */}
        {!isFreeTier && !loading && billingStatus && (
          <Layout.Section>
            <BillingStatusCard
              initialStatus={billingStatus}
              onStatusChange={handleStatusChange}
            />
          </Layout.Section>
        )}
      </Layout>
    </div>
  );
}
